import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
  MenuItem,
  DialogTitle,
  DialogActions,
  Skeleton,
  Stack
} from "@mui/material";

import { ProgressButton, SquareIconButton } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import useAutostoreOutOfStock from "~/hooks/useAutostoreOufOfStock";
import {
  mixpanelTrack,
  mixpanelSetHoldConfirm,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";

type Props = {
  handleMoveNextPick: (args: { reporter: string }) => Promise<void>;
};

function AutostoreOutOfStockDialog(props: Props) {
  const { t } = useTranslation();
  const { handleMoveNextPick } = props;
  const trackedModalName = useMixpanelModalName("Out of Stock");

  const {
    backButtonShown,
    currentPickAmount,
    outOfStockReasonCode,
    outOfStockReasonCodes,
    ableToPickQty,
    showSkeleton,
    closeModalCallback,
    handleQuantityChange,
    handleButtonClick,
    handleOutOfStockReasonCodeChange,
    confirmOutOfStock,
    backButtonCallback
  } = useAutostoreOutOfStock({
    handleMoveNextPick
  });

  return (
    <Dialog
      key="autostore-out-of-stock-dialog"
      open
      onClose={backButtonShown ? undefined : () => closeModalCallback()}
      sx={{ padding: 0, margin: 0, fontFamily: "Roboto" }}
    >
      <DialogTitle>{t("out of stock")}</DialogTitle>
      <DialogContent>
        <Box>
          <Typography sx={{ marginBottom: 3, fontSize: "1.75em" }}>
            {showSkeleton.current ? (
              <Skeleton width="450px" animation="wave" />
            ) : (
              <>
                {`${t("how many units are you")} `}
                <Box
                  component="span"
                  display="inline"
                  sx={{ color: "success.main" }}
                >
                  {`${t("able")} `}
                </Box>
                {`${t("to pick")}?`}
              </>
            )}
          </Typography>

          <Grid container sx={{ mb: 3, width: "95%", mx: "auto" }}>
            <Grid item xs={3}>
              {showSkeleton.current ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation="wave"
                />
              ) : (
                <TextField
                  id="quantity"
                  label=""
                  type="text"
                  style={{ fontSize: "2em" }}
                  variant="outlined"
                  value={ableToPickQty}
                  onChange={handleQuantityChange}
                  onBlur={() => {
                    if (ableToPickQty !== null) {
                      mixpanelTrack({
                        trackedPageName: trackedModalName,
                        type: "Input Change",
                        label: "Enter Quantity",
                        eventProperties: {
                          Quantity: ableToPickQty
                        }
                      });
                    }
                  }}
                  inputProps={{
                    style: {
                      width: "100px",
                      height: "115px",
                      fontSize: "6em",
                      textAlign: "center"
                    },
                    "aria-label": "able-to-pick-quantity"
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "6em",
                  textAlign: "center",
                  paddingLeft: 4
                }}
                id="pickAmount"
              >
                {showSkeleton.current ? (
                  <Skeleton sx={{ marginRight: "20px" }} animation="wave" />
                ) : (
                  `/  ${currentPickAmount}`
                )}
              </Typography>
            </Grid>
            <Stack spacing={2} direction="column" justifyContent="center">
              {showSkeleton.current ? (
                <Skeleton
                  variant="rounded"
                  width="50px"
                  height="50px"
                  sx={{ marginBottom: "5px" }}
                  animation="wave"
                />
              ) : (
                <SquareIconButton
                  aria-label="increment-round-button"
                  onClick={() => {
                    if (
                      ableToPickQty !== null &&
                      ableToPickQty + 1 < currentPickAmount
                    ) {
                      mixpanelTrack({
                        trackedPageName: trackedModalName,
                        type: "Button Click",
                        label: "Increase Quantity",
                        eventProperties: {
                          Quantity: ableToPickQty + 1
                        }
                      });
                    }
                    handleButtonClick(1);
                  }}
                  size="large"
                >
                  <ExpandLessIcon sx={{ fonstSize: "27px" }} />
                </SquareIconButton>
              )}
              {showSkeleton.current ? (
                <Skeleton
                  variant="rounded"
                  width="50px"
                  height="50px"
                  animation="wave"
                />
              ) : (
                <SquareIconButton
                  aria-label="decrement-round-button"
                  onClick={() => {
                    if (ableToPickQty !== null && ableToPickQty - 1 >= 0) {
                      mixpanelTrack({
                        trackedPageName: trackedModalName,
                        type: "Button Click",
                        label: "Decrease Quantity",
                        eventProperties: {
                          Quantity: ableToPickQty - 1
                        }
                      });
                    }
                    handleButtonClick(-1);
                  }}
                  size="large"
                >
                  <ExpandMoreIcon sx={{ fontSize: "27px" }} />
                </SquareIconButton>
              )}
            </Stack>
          </Grid>
          <Grid
            container
            direction="column"
            style={{
              width: "100%"
            }}
          >
            <Grid item>
              <Typography sx={{ marginBottom: "15%", fontSize: "1.75em" }}>
                {showSkeleton.current ? (
                  <Skeleton animation="wave" />
                ) : (
                  `${t("why can't you pick the full amount?")}`
                )}
              </Typography>
            </Grid>
            {showSkeleton.current ? (
              <Skeleton
                variant="rectangular"
                height="50px"
                animation="wave"
                sx={{ marginBottom: "10px" }}
              />
            ) : (
              <Grid
                item
                style={{
                  width: "100%",
                  marginTop: "-50px"
                }}
              >
                <TextField
                  fullWidth
                  value={outOfStockReasonCode}
                  onChange={(e) => {
                    handleOutOfStockReasonCodeChange(e.target.value);
                  }}
                  select
                  label={t("hold type")}
                  variant="outlined"
                  style={{ width: "100%", marginBottom: "50px" }}
                  inputProps={{
                    style: { fontSize: 30 },
                    "aria-label": "hold-types-dropdown"
                  }}
                >
                  {outOfStockReasonCodes.map((option) => (
                    <MenuItem
                      key={option.value.toLocaleLowerCase()}
                      value={option.value.toLocaleLowerCase()}
                      style={{ padding: "1.5em 0 1.5em 0.5em" }}
                      onClick={() => {
                        mixpanelTrack({
                          trackedPageName: trackedModalName,
                          type: "Select Option",
                          label: "Select Hold Type",
                          eventProperties: {
                            "Hold Type": option.value
                          }
                        });
                      }}
                    >
                      <Typography variant="h6">{option.text}</Typography>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
          <DialogActions sx={{ justifyContent: "center" }}>
            {backButtonShown && (
              <ProgressButton
                onClick={() => {
                  mixpanelTrack({
                    trackedPageName: trackedModalName,
                    type: "Button Click",
                    label: "Back"
                  });
                  backButtonCallback();
                }}
                buttonSize="medium"
                color="secondary"
                disabled={showSkeleton.current}
              >
                {t("back")}
              </ProgressButton>
            )}

            <ProgressButton
              onClick={async () => {
                mixpanelSetHoldConfirm(
                  trackedModalName,
                  "Button Click",
                  "Confirm",
                  outOfStockReasonCode
                );

                await confirmOutOfStock();
              }}
              buttonSize="medium"
              color="primary"
              responsive
              fullWidth
              disabled={showSkeleton.current}
              hideProgress
            >
              {t("confirm")}
            </ProgressButton>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
export default AutostoreOutOfStockDialog;
